import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

const BN = {
  backLink:           'Enjin Laporan',
  title:              '🏛 Papan Pemuka MOE',
  subtitle:           'Papan Pemuka Kementerian Pendidikan',
  nationalReports:    'laporan tahap nasional',
  notSeeded:          '⚠ Laporan belum diseed',
  retryButton:        'Cuba Lagi',
  districtLabel:      'Daerah / District',
  levelLabel:         'Peringkat / Level',
  dateFromLabel:      'Tarikh Mula',
  dateToLabel:        'Tarikh Akhir',
  allDistricts:       'Semua Daerah',
  allLevels:          'Semua',
  running:            'Menjanakan Laporan…',
  runButton:          '▶ Jana Laporan',
  rowsSummary:        'baris / rows',
  downloadCsv:        '⬇ CSV',
  emptyData:          'Tiada data untuk penapis yang dipilih. / No data for selected filters.',
  showingRows:        'Menunjukkan',
  ofRows:             'baris. Muat turun CSV untuk data penuh.',
  closeButton:        '✕ Tutup',
  errorNoDefinition:  'ID laporan tidak dijumpai. Sila semak seeding telah dijalankan. / Report ID not found. Please ensure seed has been run.',
};

const EN = {
  backLink:           'Report Engine',
  title:              '🏛 MOE Dashboard',
  subtitle:           'Ministry of Education Dashboard',
  nationalReports:    'national-level reports',
  notSeeded:          '⚠ Report not seeded',
  retryButton:        'Retry',
  districtLabel:      'District',
  levelLabel:         'Level',
  dateFromLabel:      'Date From',
  dateToLabel:        'Date To',
  allDistricts:       'All Districts',
  allLevels:          'All',
  running:            'Generating Report…',
  runButton:          '▶ Run Report',
  rowsSummary:        'rows',
  downloadCsv:        '⬇ CSV',
  emptyData:          'No data for selected filters.',
  showingRows:        'Showing',
  ofRows:             'rows. Download CSV for full data.',
  closeButton:        '✕ Close',
  errorNoDefinition:  'Report ID not found. Please ensure seed has been run.',
};

interface Props { user: AuthUser }

interface ReportCard {
  code: string; nameMs: string; nameEn: string; icon: string;
  definitionId: string | null;
}

interface RunResult {
  jobId: string; data: Record<string, unknown>[]; total: number; columns: string[];
}

// MOE reports shown on this dashboard — nameMs/nameEn kept as bilingual card data
const MOE_CARDS: Omit<ReportCard, 'definitionId'>[] = [
  { code: 'R01', nameMs: 'Statistik Enrolmen',     nameEn: 'Student Enrolment Statistics',   icon: '🎓' },
  { code: 'R02', nameMs: 'Kadar Kehadiran',         nameEn: 'Attendance Rates',               icon: '✅' },
  { code: 'R03', nameMs: 'Keputusan Peperiksaan',   nameEn: 'National Exam Pass Rates',       icon: '📝' },
  { code: 'R04', nameMs: 'Bilangan Pendidik',       nameEn: 'Educator Headcount',             icon: '👩‍🏫' },
  { code: 'R05', nameMs: 'Pencapaian CPD',          nameEn: 'CPD Completion Rates',           icon: '📚' },
  { code: 'R06', nameMs: 'Pelajar Keperluan Khas',  nameEn: 'SEN Distribution',              icon: '♿' },
  { code: 'R08', nameMs: 'Sekolah Swasta DPE',      nameEn: 'Private School DPE Compliance', icon: '🏫' },
  { code: 'R09', nameMs: 'Sekolah MORA',            nameEn: 'MORA Schools Overview',         icon: '🕌' },
];

export function MOEDashboardPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;

  const [definitions, setDefinitions] = useState<Record<string, string>>({}); // code → id
  const [defsLoading, setDefsLoading] = useState(true);
  const [defsError,   setDefsError]   = useState<string | null>(null);
  const [toast,       setToast]       = useState<{ message: string; variant: ToastVariant } | null>(null);

  // Active panel: run a single report
  const [activeCard,  setActiveCard]  = useState<ReportCard | null>(null);
  const [running,     setRunning]     = useState(false);
  const [runResult,   setRunResult]   = useState<RunResult | null>(null);
  const [runError,    setRunError]    = useState<string | null>(null);
  const [filters, setFilters] = useState({ district: '', dateFrom: '', dateTo: '', level: '' });

  const load = useCallback(async () => {
    setDefsLoading(true); setDefsError(null);
    try {
      const defs = await apiGet<Array<{ id: string; report_code: string | null }>>('/api/reports');
      const map: Record<string, string> = {};
      for (const d of defs) {
        if (d.report_code) map[d.report_code] = d.id;
      }
      setDefinitions(map);
    } catch (err) {
      setDefsError(err instanceof Error ? err.message : 'Gagal memuatkan laporan. / Failed to load reports.');
    } finally { setDefsLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function openCard(card: Omit<ReportCard, 'definitionId'>) {
    const id = definitions[card.code] ?? null;
    setActiveCard({ ...card, definitionId: id });
    setRunResult(null); setRunError(null);
    setFilters({ district: '', dateFrom: '', dateTo: '', level: '' });
  }

  function validateFilters(): string | null {
    if (filters.dateFrom && filters.dateTo && filters.dateTo < filters.dateFrom)
      return 'Tarikh akhir mesti selepas tarikh mula. / End date must be after start date.';
    return null;
  }

  async function handleRun() {
    if (!activeCard?.definitionId) {
      setRunError(UI.errorNoDefinition);
      return;
    }
    const err = validateFilters();
    if (err) { setRunError(err); return; }
    setRunning(true); setRunError(null); setRunResult(null);
    try {
      const f: Record<string, string> = {};
      if (filters.district) f['district'] = filters.district;
      if (filters.dateFrom) f['dateFrom'] = filters.dateFrom;
      if (filters.dateTo)   f['dateTo']   = filters.dateTo;
      if (filters.level)    f['level']    = filters.level;
      const result = await apiPost<RunResult>(`/api/reports/${activeCard.definitionId}/run`, {
        filters: Object.keys(f).length ? f : undefined,
      });
      setRunResult(result);
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Laporan gagal dijalankan. / Report failed.');
    } finally { setRunning(false); }
  }

  function downloadCsv(columns: string[], data: Record<string, unknown>[], filename: string) {
    const header = columns.join(',');
    const rows = data.map((row) =>
      columns.map((col) => {
        const v = row[col] ?? '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '1060px', margin: '0 auto' }}>
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/admin/reports" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← {UI.backLink}</Link>
          </div>

          <div style={{ marginBottom: spacing.xl }}>
            <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
              {UI.title}
            </h1>
            <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
              {UI.subtitle} — {MOE_CARDS.length} {UI.nationalReports}
            </p>
          </div>

          {defsError && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
              <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{defsError}</p>
              <Button variant="ghost" size="sm" onClick={() => void load()} style={{ marginTop: spacing.xs }}>{UI.retryButton}</Button>
            </div>
          )}

          {defsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: spacing.md }}>
              {MOE_CARDS.map((card) => {
                const hasId  = !!definitions[card.code];
                const isActive = activeCard?.code === card.code;
                return (
                  <button
                    key={card.code}
                    onClick={() => openCard(card)}
                    style={{
                      backgroundColor: isActive ? 'rgba(247,208,0,0.1)' : colors.neutral.charcoal,
                      border: `1px solid ${isActive ? colors.primary.yellow : colors.neutral.gray800}`,
                      borderRadius: borderRadius.xl, padding: spacing.lg, textAlign: 'left',
                      cursor: 'pointer', boxShadow: shadows.lg, fontFamily: typography.fontFamily.body,
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: spacing.sm }}>{card.icon}</div>
                    <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.primary.yellow, fontWeight: typography.fontWeight.bold }}>{card.code}</p>
                    <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{card.nameMs}</p>
                    <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{card.nameEn}</p>
                    {!hasId && <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: '#fb923c' }}>{UI.notSeeded}</p>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Active report panel */}
          {activeCard && (
            <div style={{ marginTop: spacing.xl, backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, boxShadow: shadows.xl }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.lg }}>
                <div>
                  <p style={{ margin: 0, fontWeight: typography.fontWeight.bold, color: colors.primary.yellow, fontSize: typography.fontSize.xs }}>{activeCard.code}</p>
                  <p style={{ margin: `2px 0 0`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.lg }}>{activeCard.nameMs}</p>
                  <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{activeCard.nameEn}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setActiveCard(null); setRunResult(null); setRunError(null); }}>{UI.closeButton}</Button>
              </div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.md }}>
                <LF label={UI.districtLabel} style={{ flex: '1 1 140px' }}>
                  <select value={filters.district} onChange={(e) => setFilters((f) => ({ ...f, district: e.target.value }))} disabled={running} style={inputSt()}>
                    <option value="">{UI.allDistricts}</option>
                    {['brunei-muara', 'tutong', 'belait', 'temburong'].map((d) => (
                      <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                    ))}
                  </select>
                </LF>
                <LF label={UI.levelLabel} style={{ flex: '1 1 120px' }}>
                  <select value={filters.level} onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value }))} disabled={running} style={inputSt()}>
                    <option value="">{UI.allLevels}</option>
                    {['primary', 'secondary', 'tertiary'].map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </LF>
                <LF label={UI.dateFromLabel} style={{ flex: '1 1 140px' }}>
                  <input type="date" value={filters.dateFrom} max={filters.dateTo || undefined} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))} disabled={running} style={inputSt()} />
                </LF>
                <LF label={UI.dateToLabel} style={{ flex: '1 1 140px' }}>
                  <input type="date" value={filters.dateTo} min={filters.dateFrom || undefined} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))} disabled={running} style={inputSt()} />
                </LF>
              </div>

              {runError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{runError}</p>}

              <Button variant="primary" size="sm" onClick={() => void handleRun()} disabled={running || !activeCard.definitionId} loading={running}>
                {running ? UI.running : `${UI.runButton} ${activeCard.code}`}
              </Button>

              {/* Results table */}
              {runResult && (
                <div style={{ marginTop: spacing.lg }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm, flexWrap: 'wrap', gap: spacing.sm }}>
                    <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                      ✅ {runResult.total} {UI.rowsSummary}
                    </p>
                    {runResult.total > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => downloadCsv(runResult.columns, runResult.data, `${activeCard.code}_${new Date().toISOString().slice(0, 10)}.csv`)}>
                        {UI.downloadCsv}
                      </Button>
                    )}
                  </div>
                  {runResult.total === 0 ? (
                    <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: borderRadius.lg, padding: spacing.lg, textAlign: 'center' }}>
                      <p style={{ margin: 0, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                        {UI.emptyData}
                      </p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto', borderRadius: borderRadius.lg, border: `1px solid ${colors.neutral.gray800}` }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: typography.fontSize.xs }}>
                        <thead>
                          <tr style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                            {runResult.columns.map((col) => (
                              <th key={col} style={{ padding: `${spacing.xs} ${spacing.sm}`, textAlign: 'left', color: colors.neutral.gray400, fontWeight: typography.fontWeight.semibold, whiteSpace: 'nowrap', borderBottom: `1px solid ${colors.neutral.gray800}` }}>
                                {col.replace(/_/g, ' ').toUpperCase()}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {runResult.data.slice(0, 200).map((row, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${colors.neutral.gray800}`, backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                              {runResult.columns.map((col) => (
                                <td key={col} style={{ padding: `${spacing.xs} ${spacing.sm}`, color: colors.neutral.white, whiteSpace: 'nowrap' }}>
                                  {row[col] === null || row[col] === undefined
                                    ? <span style={{ color: colors.neutral.gray600 }}>—</span>
                                    : String(row[col])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {runResult.total > 200 && (
                        <p style={{ margin: `${spacing.xs} ${spacing.sm}`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                          {UI.showingRows} 200/{runResult.total} {UI.ofRows}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function LF({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={style}><label style={labelSt}>{label}</label>{children}</div>;
}
const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return { width: '100%', backgroundColor: colors.neutral.black, border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md, color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`, fontSize: typography.fontSize.sm, outline: 'none', boxSizing: 'border-box', fontFamily: typography.fontFamily.body };
}
